import { useState } from 'react'
import { motion } from 'framer-motion'
import { 
  FileText, 
  Triangle, 
  Map,
  Download,
  Copy,
  Check,
  Loader2,
  ChevronRight,
  X
} from 'lucide-react'
import { clsx } from 'clsx'
import api from '@/services/api'

interface ArtifactGeneratorProps {
  dealId: string
  dealName: string
}

type ArtifactType = 'one_pager' | 'decision_triangle' | 'visit_canvas'

interface ArtifactConfig {
  type: ArtifactType
  name: string
  description: string
  icon: React.ElementType
  color: string
  bgColor: string
  needsInput?: boolean
}

const ARTIFACT_CONFIGS: ArtifactConfig[] = [
  {
    type: 'one_pager',
    name: '组织层闭环一页纸',
    description: '用EB语言阐述业务价值、ROI、窗口期压力',
    icon: FileText,
    color: 'text-blue-600',
    bgColor: 'bg-blue-50',
  },
  {
    type: 'decision_triangle',
    name: '决策三角形翻译表',
    description: 'ROI/窗口期/机会成本三维度话术模板',
    icon: Triangle,
    color: 'text-purple-600',
    bgColor: 'bg-purple-50',
  },
  {
    type: 'visit_canvas',
    name: '拜访画布',
    description: '拜访准备清单、问题设计、目标设定',
    icon: Map,
    color: 'text-green-600',
    bgColor: 'bg-green-50',
    needsInput: true,
  },
]

export default function ArtifactGenerator({ dealId, dealName }: ArtifactGeneratorProps) {
  const [selectedType, setSelectedType] = useState<ArtifactType | null>(null)
  const [isGenerating, setIsGenerating] = useState(false)
  const [generatedContent, setGeneratedContent] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)
  const [error, setError] = useState<string | null>(null)
  
  // 拜访画布额外输入
  const [visitTarget, setVisitTarget] = useState('')
  const [visitPurpose, setVisitPurpose] = useState('')

  const handleGenerate = async (type: ArtifactType) => {
    setIsGenerating(true)
    setError(null)
    setGeneratedContent(null)
    
    try {
      let response
      
      switch (type) {
        case 'one_pager':
          response = await api.post(`/artifacts/deal/${dealId}/one-pager`)
          break
        case 'decision_triangle':
          response = await api.post(`/artifacts/deal/${dealId}/decision-triangle`)
          break
        case 'visit_canvas':
          if (!visitTarget || !visitPurpose) {
            setError('请填写拜访对象和拜访目的')
            setIsGenerating(false)
            return
          }
          response = await api.post(`/artifacts/deal/${dealId}/visit-canvas`, {
            visit_target: visitTarget,
            visit_purpose: visitPurpose,
          })
          break
      }
      
      if (response?.data?.markdownContent) {
        setGeneratedContent(response.data.markdownContent)
      } else if (response?.data?.error) {
        setError(response.data.error)
      }
    } catch (err: any) {
      setError(err.message || '生成失败，请重试')
    } finally {
      setIsGenerating(false)
    }
  }

  const handleCopy = async () => {
    if (generatedContent) {
      await navigator.clipboard.writeText(generatedContent)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  const handleDownload = () => {
    if (generatedContent) {
      const blob = new Blob([generatedContent], { type: 'text/markdown' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${dealName}_${selectedType}_${new Date().toISOString().split('T')[0]}.md`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    }
  }

  const selectedConfig = ARTIFACT_CONFIGS.find(c => c.type === selectedType)

  return (
    <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
      {/* 头部 */}
      <div className="px-6 py-4 border-b border-gray-100">
        <h2 className="text-lg font-semibold text-gray-900">输出件生成器</h2>
        <p className="text-sm text-gray-500 mt-0.5">
          为 {dealName} 生成销售输出件
        </p>
      </div>

      <div className="p-6">
        {/* 选择输出件类型 */}
        {!selectedType && (
          <div className="space-y-3">
            {ARTIFACT_CONFIGS.map((config) => {
              const Icon = config.icon
              return (
                <motion.button
                  key={config.type}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  onClick={() => setSelectedType(config.type)}
                  className="w-full flex items-center justify-between p-4 rounded-xl border-2 border-gray-100 hover:border-gray-200 transition-all text-left group"
                >
                  <div className="flex items-center gap-4">
                    <div className={clsx('p-3 rounded-xl', config.bgColor)}>
                      <Icon className={clsx('w-5 h-5', config.color)} />
                    </div>
                    <div>
                      <div className="font-medium text-gray-900">{config.name}</div>
                      <div className="text-sm text-gray-500">{config.description}</div>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-gray-600 transition-colors" />
                </motion.button>
              )
            })}
          </div>
        )}

        {/* 选中后的生成界面 */}
        {selectedType && !generatedContent && (
          <div>
            {/* 返回按钮 */}
            <button
              onClick={() => {
                setSelectedType(null)
                setError(null)
              }}
              className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-4"
            >
              <ChevronRight className="w-4 h-4 rotate-180" />
              返回选择
            </button>

            {/* 选中的类型信息 */}
            {selectedConfig && (
              <div className={clsx('p-4 rounded-xl mb-6', selectedConfig.bgColor)}>
                <div className="flex items-center gap-3">
                  <selectedConfig.icon className={clsx('w-6 h-6', selectedConfig.color)} />
                  <div>
                    <div className="font-medium text-gray-900">{selectedConfig.name}</div>
                    <div className="text-sm text-gray-600">{selectedConfig.description}</div>
                  </div>
                </div>
              </div>
            )}

            {/* 拜访画布额外输入 */}
            {selectedType === 'visit_canvas' && (
              <div className="space-y-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    拜访对象 *
                  </label>
                  <input
                    type="text"
                    value={visitTarget}
                    onChange={(e) => setVisitTarget(e.target.value)}
                    placeholder="例如：张总（IT部门负责人）"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    拜访目的 *
                  </label>
                  <input
                    type="text"
                    value={visitPurpose}
                    onChange={(e) => setVisitPurpose(e.target.value)}
                    placeholder="例如：确认技术评估标准"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              </div>
            )}

            {/* 错误提示 */}
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                {error}
              </div>
            )}

            {/* 生成按钮 */}
            <button
              onClick={() => handleGenerate(selectedType)}
              disabled={isGenerating}
              className={clsx(
                'w-full py-3 rounded-xl font-medium flex items-center justify-center gap-2 transition-colors',
                isGenerating
                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  : 'bg-primary-600 text-white hover:bg-primary-700'
              )}
            >
              {isGenerating ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  正在生成...
                </>
              ) : (
                <>
                  生成输出件
                </>
              )}
            </button>
          </div>
        )}

        {/* 生成结果展示 */}
        {generatedContent && (
          <div>
            {/* 操作栏 */}
            <div className="flex items-center justify-between mb-4">
              <button
                onClick={() => {
                  setGeneratedContent(null)
                  setSelectedType(null)
                }}
                className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
              >
                <X className="w-4 h-4" />
                关闭
              </button>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleCopy}
                  className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  {copied ? (
                    <>
                      <Check className="w-4 h-4 text-green-500" />
                      <span className="text-green-600">已复制</span>
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4" />
                      复制
                    </>
                  )}
                </button>
                <button
                  onClick={handleDownload}
                  className="flex items-center gap-1 px-3 py-1.5 text-sm bg-primary-100 text-primary-700 hover:bg-primary-200 rounded-lg transition-colors"
                >
                  <Download className="w-4 h-4" />
                  下载
                </button>
              </div>
            </div>

            {/* Markdown 内容预览 */}
            <div className="max-h-[60vh] overflow-y-auto p-4 bg-gray-50 rounded-xl">
              <pre className="whitespace-pre-wrap text-sm text-gray-700 font-mono leading-relaxed">
                {generatedContent}
              </pre>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

